import { useMutation } from '@tanstack/react-query';
import type { AxiosResponse } from 'axios';
import { toast } from '@/hooks/use-toast';
import { preRegistrationApi } from '../api/pre-registration.api';
import { getBlobApiErrorMessage } from '../utils/api-error.utils';
import type { PreRegisteredAccommodationExportParams } from '../types/pre-registration.types';

const XLSX_MIME = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
const FALLBACK_FILE_NAME = 'alojamientos-preinscriptos.xlsx';

const getFileName = (response: AxiosResponse<Blob>): string => {
  const disposition = String(response.headers['content-disposition'] ?? '');
  const match = disposition.match(/filename="([^"]+)"/);
  return match ? decodeURIComponent(match[1]) : FALLBACK_FILE_NAME;
};

const downloadBlob = (data: Blob, fileName: string) => {
  const url = window.URL.createObjectURL(new Blob([data], { type: XLSX_MIME }));
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

// "Exportar a Excel" of the pre-registered accommodations (list page and municipal list)
export const usePreRegisteredAccommodationExport = () => {
  const mutation = useMutation({
    mutationFn: (params: PreRegisteredAccommodationExportParams) =>
      preRegistrationApi.exportAccommodations(params),
    onSuccess: (response) => {
      downloadBlob(response.data, getFileName(response));
      toast({
        title: 'Descarga completada',
        description: 'El archivo Excel se descargó correctamente.',
      });
    },
    onError: async (error: unknown) => {
      const description = await getBlobApiErrorMessage(
        error,
        'Ocurrió un error al generar el archivo Excel.'
      );
      toast({ title: 'Error al exportar', description, variant: 'destructive' });
    },
  });

  return { exportToExcel: mutation.mutate, isExporting: mutation.isPending };
};
